import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

const DASHBOARD_KEY = 'dashboard-token';
export type DashboardTheme = 'sofco-theme-dark' | 'sofco-theme-light';
export type DashboardDensity = 0 | -1 | -2 | -3 | -4 | -5;

const DASHBOARD_THEMES: DashboardTheme[] = ['sofco-theme-dark', 'sofco-theme-light'];
const DASHBOARD_DENSITIES: DashboardDensity[] = [0, -1, -2, -3, -4, -5];

@Injectable({
  providedIn: 'root',
})
export class DashboardSettingsService {
  private defaultSettings: DashboardSettings = {
    theme: 'sofco-theme-dark',
    density: 0,
    enableSubCollectionsInList: false,
  };

  public dashboardSettings = new BehaviorSubject<DashboardSettings>(this.getSettings());

  public isMobile = new BehaviorSubject(false);
  /**
   * If true the device is a mobile
   */
  isHandset$: Observable<boolean>;

  public isFullscreen = toSignal(this.dashboardSettings.pipe(map(settings => settings.fullscreen ?? false)));
  public isLeftRailMode = toSignal(this.dashboardSettings.pipe(map(settings => settings.leftRailMode ?? false)));
  public isMobleSignal = toSignal(this.isMobile.asObservable());

  constructor(private breakpointObserver: BreakpointObserver) {
    this.isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
      map(result => result.matches),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.isHandset$.subscribe(isHandset => {
      this.isMobile.next(isHandset);
    });
  }

  public saveSettings(settings: DashboardSettings): void {
    window.sessionStorage.removeItem(DASHBOARD_KEY);
    window.sessionStorage.setItem(DASHBOARD_KEY, JSON.stringify(settings));
    this.dashboardSettings.next(settings);
  }

  public getSettings(): DashboardSettings {
    let settingsRaw = window.sessionStorage.getItem(DASHBOARD_KEY);
    if (!settingsRaw) {
      return this.defaultSettings;
    }

    const settings = JSON.parse(settingsRaw) as Partial<DashboardSettings>;
    return {
      ...this.defaultSettings,
      ...settings,
      theme: DASHBOARD_THEMES.includes(settings.theme) ? settings.theme : this.defaultSettings.theme,
      density: DASHBOARD_DENSITIES.includes(settings.density) ? settings.density : this.defaultSettings.density,
    };
  }

  public changeTheme(): void {
    let settings = this.getSettings();
    if (settings.theme === 'sofco-theme-dark') {
      settings.theme = 'sofco-theme-light';
    } else {
      settings.theme = 'sofco-theme-dark';
    }
    this.saveSettings(settings);
  }

  public setTheme(theme: DashboardTheme): void {
    this.saveSettings({
      ...this.getSettings(),
      theme,
    });
  }

  public setDensity(density: DashboardDensity): void {
    this.saveSettings({
      ...this.getSettings(),
      density,
    });
  }

  public toggleSubCollectionsInList(value: boolean = null) {
    let settings = this.getSettings();
    if (value === null) {
      settings.enableSubCollectionsInList = !settings.enableSubCollectionsInList;
    } else {
      settings.enableSubCollectionsInList = value;
    }
    this.saveSettings(settings);
  }

  toggleFullscreen(value: boolean = null) {
    let settings = this.getSettings();
    if (value === null) {
      settings.fullscreen = !settings.fullscreen;
    } else {
      settings.fullscreen = value;
    }
    this.saveSettings(settings);
  }

  toggleRailMode(value: boolean = null) {
    let settings = this.getSettings();
    if (value === null) {
      settings.leftRailMode = !settings.leftRailMode;
    } else {
      settings.leftRailMode = value;
    }
    this.saveSettings(settings);
  }
}

export interface DashboardSettings {
  theme: DashboardTheme;
  density?: DashboardDensity;
  enableSubCollectionsInList: boolean;
  leftRailMode?: boolean;
  fullscreen?: boolean;
}
